import { useMemo, useState } from 'react'

import { InvoiceModel } from '@/Invoices/domain'

export type Item = {
  active: boolean
  id: number
  isInvoiceDownloadeable: boolean
  hasInvoiceAvailable: boolean
  hasTransactionAvailable: boolean
}

type IUseBatchCheckboxes = {
  selected: Item[]
  indeterminate: boolean
  massiveSelect: boolean
  handleSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleSelect: (id: number) => void
  init: (invoices: InvoiceModel[]) => void
  selectedStoredItems: Item[]
}

export const useBatchCheckboxes = (): IUseBatchCheckboxes => {
  const [selected, setSelected] = useState<Item[]>([])

  const activeItems = selected.filter(item => item.active)

  const isIndeterminate = (): boolean =>
    activeItems.length > 0 && activeItems.length < selected.length

  const isMassiveSelected = (): boolean => activeItems.length === selected.length

  const selectedStoredItems: Item[] = JSON.parse(
    sessionStorage.getItem('activeInvoices') || '[]',
  )

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newSelectedState = event.target.checked
    setSelected(
      selected.map(item => ({
        ...item,
        active: newSelectedState,
      })),
    )

    // Retrieve and parse the current list of active items from sessionStorage
    let activeItems: Item[] = JSON.parse(sessionStorage.getItem('activeInvoices') || '[]')

    if (newSelectedState) {
      // Add all selected items, avoiding duplicates based on item id
      const allActiveItems = selected.map(item => ({ ...item, active: true }))
      const mergedItems = [...activeItems, ...allActiveItems]
      const uniqueItems = Array.from(
        new Map(mergedItems.map(item => [item.id, item])).values(),
      )
      activeItems = uniqueItems
    } else {
      // Remove the items of all items currently in 'selected' from the sessionStorage
      const deselectIds = new Set(selected.map(item => item.id))
      activeItems = activeItems.filter(item => !deselectIds.has(item.id))
    }

    // Save the updated list of active items back to sessionStorage
    sessionStorage.setItem('activeInvoices', JSON.stringify(activeItems))
  }

  const handleSelect = (id: number): void => {
    // Retrieve and parse the current list of active items from sessionStorage
    const activeItems: Item[] = JSON.parse(
      sessionStorage.getItem('activeInvoices') || '[]',
    )

    // Update the selected state for the items
    const newSelected = selected.map(item => ({
      ...item,
      active: item.id === id ? !item.active : item.active,
    }))

    setSelected(newSelected)

    // Find the item that was just selected/deselected
    const selectedItem = newSelected.find(item => item.id === id)

    // Determine if the selected item should be added or removed from the activeItems list
    const index = activeItems.findIndex(item => item.id === id)
    if (selectedItem?.active && index === -1) {
      // Add to the list if selected and not already present
      activeItems.push(selectedItem)
    } else if (!selectedItem?.active && index !== -1) {
      // Remove from the list if deselected
      activeItems.splice(index, 1)
    }

    // Save the updated list of active items back to sessionStorage
    sessionStorage.setItem('activeInvoices', JSON.stringify(activeItems))
  }

  const init = (invoices: InvoiceModel[]): void => {
    // Retrieve and parse the list of active items from sessionStorage
    const activeItems: Item[] = JSON.parse(
      sessionStorage.getItem('activeInvoices') || '[]',
    )

    // Set the selected state with the invoices, marking them as active if their object is in the activeItems array
    setSelected(
      invoices.map(invoice => ({
        id: invoice.invoiceId,
        active: activeItems.some(item => item.id === invoice.invoiceId),
        isInvoiceDownloadeable: invoice.isInvoiceDownloadable,
        hasInvoiceAvailable: invoice.isInvoiceReadyToDownload,
        hasTransactionAvailable: invoice.isTransactionReadyToDownload,
      })),
    )
  }

  return {
    selected: activeItems,
    indeterminate: useMemo(() => isIndeterminate(), [activeItems.length]),
    massiveSelect: useMemo(
      () => isMassiveSelected(),
      [activeItems.length, selected.length],
    ),
    selectedStoredItems,
    handleSelect,
    handleSelectAllClick,
    init,
  }
}
